import { Link } from "react-router-dom";
import { Check, Crown } from "lucide-react";
import { planService } from "@/services/planService";

/**
 * "Quick Comparison" pricing preview shown just below the homepage hero.
 * Pulls real limits from planService rather than hardcoding numbers so it
 * can't silently drift out of sync with the actual plan definitions used
 * everywhere else (PricingPage, ConvertPage limit checks, etc.).
 */
export default function QuickComparisonCard() {
  const guestPlan = planService.getPlanDetails("guest");
  const proPlan = planService.getPlanDetails("pro");

  const freeConversionsPerDay = guestPlan?.limits.dailyConversions ?? 5;
  const proConversionsPerMonth = proPlan?.limits.monthlyConversions ?? 6000;

  return (
    <section className="py-6 sm:py-8 bg-white">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
          {/* Free tier */}
          <div className="p-5 sm:p-6">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Free
            </div>
            <div className="flex items-baseline gap-1 mb-3">
              <span className="text-2xl font-bold text-gray-900">
                {freeConversionsPerDay}
              </span>
              <span className="text-sm text-gray-600">
                conversions/day, no account needed
              </span>
            </div>
            <ul className="space-y-1.5 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                432+ formats supported
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                Standard processing speed
              </li>
            </ul>
          </div>

          {/* Pro tier */}
          <div className="p-5 sm:p-6 bg-gradient-to-br from-primary/5 to-brand-50">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-primary uppercase tracking-wide mb-2">
              <Crown className="w-4 h-4" />
              Pro
            </div>
            <div className="flex items-baseline gap-1 mb-3">
              <span className="text-2xl font-bold text-gray-900">
                {proConversionsPerMonth.toLocaleString()}
              </span>
              <span className="text-sm text-gray-600">
                conversions/mo + priority speed
              </span>
            </div>
            <ul className="space-y-1.5 text-sm text-gray-700 mb-4">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                Up to 100 concurrent conversions
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                Larger file size limits
              </li>
            </ul>
            <Link
              to="/pricing"
              className="inline-flex items-center text-sm font-semibold text-primary hover:text-primary/80"
            >
              See Pro plans →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
